import { useEffect, useState } from 'react'
import { api, type MarketProfile, type SeedResult } from '../api'

/**
 * The starter vocabulary for a market — TECHNICAL_DESIGN.md §5.3.3.
 *
 * §5.3.3 seeds a starter set *"so the first customer is not staring at five
 * empty tables"*. It shipped as `anubandh seed-profile <market>` and nothing
 * else, so the choice existed only for whoever installed the appliance, once.
 *
 * **One market at a time, chosen and then read.** A table of every profile with
 * a button on each row invites picking one from a summary; this is a decision
 * about what vocabulary a firm starts with, and the terms themselves are what
 * somebody should be looking at when they make it. So: choose a market, read
 * what it would add, add it.
 *
 * **A convenience, not a constraint.** Every entry seeded is editable
 * afterwards, each is superseded rather than edited in place, and nothing
 * already defined is touched — an existing term is the customer's, possibly
 * meaning something else, and overwriting it would rewrite what every rule
 * citing it has always meant.
 *
 * **An approver or an administrator may seed.** This was `dictionary.edit`
 * alone at first, on the argument that seeding defines what limits can mean. But
 * the console command it wraps needs no capability at all and the administrator
 * is the person at the console: the gate stopped nobody and cost the setup path
 * its screen. Redefining a term already in use is a different act and stays with
 * the approver.
 */
export function StarterProfiles({ canSeed }: { canSeed: boolean }) {
  const [profiles, setProfiles] = useState<MarketProfile[]>([])
  const [chosen, setChosen] = useState<string>('')
  const [result, setResult] = useState<SeedResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api
      .marketProfiles()
      .then((found) => {
        setProfiles(found)
        // Nothing preselected: a market chosen for somebody is a market nobody
        // read before adding.
        setChosen('')
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
  }, [])

  const profile = profiles.find((p) => p.name === chosen)

  async function seed() {
    if (!profile) return
    setBusy(true)
    setError(null)
    setResult(null)
    try {
      setResult(await api.seedMarketProfile(profile.name))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (profiles.length === 0) return null

  return (
    <section>
      <header className="page-head">
        <h2>Starter vocabulary by market</h2>
        <p className="note">
          A starting point, so a new appliance is not a set of empty tables.
          Everything it adds is yours to edit afterwards, and anything you have
          already defined is left exactly as it is, so no existing limit changes
          meaning.
        </p>
      </header>

      {error && <p className="error">{error}</p>}

      {!canSeed && (
        <p className="note">
          Your role cannot add vocabulary. An approver or an administrator can —
          adding a term decides what limits this appliance is able to express.
        </p>
      )}

      <label>
        Market
        <select value={chosen} onChange={(e) => setChosen(e.target.value)}>
          <option value="">Choose a market…</option>
          {profiles.map((p) => (
            <option key={p.name} value={p.name}>
              {p.title}
            </option>
          ))}
        </select>
      </label>

      {profile && (
        <div className="card">
          <h3>{profile.title}</h3>
          <p className="note">{profile.description}</p>

          <table className="grid">
            <thead>
              <tr>
                <th>What it adds</th>
                <th>How many</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(profile.counts).map(([kind, n]) => (
                <tr key={kind}>
                  <td>{kind.replace(/_/g, ' ')}</td>
                  <td className="limit">{n}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="actions">
            <button disabled={!canSeed || busy} onClick={() => void seed()}>
              {busy ? 'Adding…' : `Add ${profile.title} to this appliance`}
            </button>
          </div>

          {result && result.profile === profile.name && (
            <div className="callout">
              <h3>
                {result.total_added} added, {result.total_skipped} left alone
              </h3>
              <ul className="hits">
                {Object.entries(result.added).map(([kind, n]) => (
                  <li key={kind}>
                    added <strong>{n}</strong> {kind.replace(/_/g, ' ')}
                  </li>
                ))}
                {Object.entries(result.skipped).map(([kind, n]) => (
                  <li key={kind} className="clause">
                    left alone {n} {kind.replace(/_/g, ' ')} — already defined here
                  </li>
                ))}
              </ul>
              <p className="note">
                Nothing existing was overwritten. Anything left alone was already
                in your store, and is still whatever you defined it to be.
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
